import React, { useState } from 'react';
import { Box, Paper, Typography, Button, Collapse, alpha, useTheme } from '@mui/material';
import {
  SystemUpdateAlt as UpdateIcon,
  Schedule as LaterIcon,
  NewReleases as NewReleasesIcon,
} from '@mui/icons-material';

/**
 * Parses release notes string into bullet points.
 * Supports:
 * - Lines starting with "- " or "• "
 * - Lines separated by newlines
 * - Single line (returns as-is)
 */
const parseReleaseNotes = (notes) => {
  if (!notes) return [];

  // Split by newlines and filter empty lines
  const lines = notes.split('\n').map(l => l.trim()).filter(Boolean);

  // If single line without bullet prefix, return as single item
  if (lines.length === 1 && !lines[0].match(/^[-•]\s/)) {
    return [lines[0]];
  }

  // Parse bullet points (remove leading - or •)
  return lines.map(line => line.replace(/^[-•]\s*/, ''));
};

/**
 * Compact update banner shown when a new version is deployed.
 * Displays version number, release notes as bullet points, and action buttons.
 */
const UpdateBanner = ({ versionInfo, onDismiss, onUpdateNow }) => {
  const theme = useTheme();
  const [slideIn, setSlideIn] = useState(true);
  const isDark = theme.palette.mode === 'dark';

  const handleUpdateNow = () => {
    if (onUpdateNow) onUpdateNow();
  };

  const handleUpdateLater = () => {
    setSlideIn(false);
    setTimeout(() => {
      if (onDismiss) onDismiss();
    }, 300);
  };

  const accentColor = theme.palette.info.main;
  const bgPrimary = isDark ? '#0d1b2a' : '#002855';
  const bgSecondary = isDark ? '#1b2838' : '#004080';

  const releaseNotes = parseReleaseNotes(versionInfo?.notes);
  const hasMultipleNotes = releaseNotes.length > 1;

  return (
    <Collapse in={slideIn} timeout={300}>
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9998,
          width: { xs: 'calc(100% - 32px)', sm: 360 },
          maxWidth: { xs: 'calc(100% - 32px)', sm: 360 },
        }}
      >
        <Paper
          elevation={16}
          sx={{
            background: `linear-gradient(145deg, ${bgPrimary} 0%, ${bgSecondary} 100%)`,
            backdropFilter: 'blur(20px) saturate(180%)',
            border: `1px solid ${alpha(accentColor, 0.3)}`,
            borderRadius: 3,
            boxShadow: `
              0 12px 40px ${alpha(bgPrimary, 0.5)},
              0 4px 16px ${alpha(accentColor, 0.15)},
              inset 0 1px 0 ${alpha('#fff', 0.08)}
            `,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 2,
              borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: alpha(accentColor, 0.2),
                flexShrink: 0,
              }}
            >
              <NewReleasesIcon sx={{ fontSize: 22, color: accentColor }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.3,
                }}
              >
                New Update Available
              </Typography>
              {versionInfo?.tag && (
                <Typography
                  sx={{
                    color: alpha('#fff', 0.7),
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    mt: 0.25,
                  }}
                >
                  Version {versionInfo.tag}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Release Notes */}
          <Box sx={{ px: 2.5, py: 2 }}>
            {releaseNotes.length > 0 ? (
              hasMultipleNotes ? (
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.5,
                    listStyleType: 'disc',
                    '& li': {
                      color: alpha('#fff', 0.85),
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      mb: 0.5,
                      '&:last-child': { mb: 0 },
                      '&::marker': {
                        color: accentColor,
                      },
                    },
                  }}
                >
                  {releaseNotes.map((note, idx) => (
                    <li key={idx}>{note}</li>
                  ))}
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: alpha('#fff', 0.85),
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                  }}
                >
                  {releaseNotes[0]}
                </Typography>
              )
            ) : (
              <Typography
                sx={{
                  color: alpha('#fff', 0.85),
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                }}
              >
                Improvements and bug fixes. Update when you're ready.
              </Typography>
            )}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              px: 2.5,
              py: 2,
              pt: 0,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<LaterIcon sx={{ fontSize: 18 }} />}
              onClick={handleUpdateLater}
              sx={{
                flex: 1,
                color: '#fff',
                borderColor: alpha('#fff', 0.3),
                py: 1,
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: alpha('#fff', 0.5),
                  backgroundColor: alpha('#fff', 0.08),
                },
              }}
            >
              Later
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<UpdateIcon sx={{ fontSize: 18 }} />}
              onClick={handleUpdateNow}
              sx={{
                flex: 1,
                background: accentColor,
                color: '#fff',
                py: 1,
                fontWeight: 700,
                fontSize: '0.8125rem',
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: `0 4px 12px ${alpha(accentColor, 0.4)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: alpha(accentColor, 0.9),
                  boxShadow: `0 6px 16px ${alpha(accentColor, 0.5)}`,
                },
              }}
            >
              Update Now
            </Button>
          </Box>
        </Paper>
      </Box>
    </Collapse>
  );
};

export default UpdateBanner;
